// utils/informePdf.js
// PDF de Informes, salida APAISADA (A4 horizontal), generado con jsPDF y texto real
// (no html2canvas): sale nítido al imprimir y las barras son vectores, no píxeles.
//
// Columnas fijas (las que pidió el área): Expediente de proyecto, Organización,
// Nombre de la obra, Monto (con el desglose de adenda igual que en el informe por
// partido), Avance físico y financiero (barras) y Ubicación.
//
// El encabezado y el pie son las MISMAS imágenes A4 que usa el informe de
// organizaciones, escaladas al ancho de la hoja apaisada.
import jsPDF from 'jspdf';
import encabezadoImg from '../components/informes/encabezado_A4.png';
import pieImg from '../components/informes/pie_hoja_A4.png';

const ANCHO_PAGINA = 297; // A4 apaisado, mm
const ALTO_PAGINA = 210;
const MARGEN_H = 10;
const MARGEN_TOP = 10;
const MARGEN_BOTTOM = 10;
const ANCHO_UTIL = ANCHO_PAGINA - 2 * MARGEN_H; // 277mm

const PAD_CELDA_H = 1.5; // padding horizontal de cada celda
const PAD_CELDA_V = 1.2; // padding vertical de cada celda
const LINE_HEIGHT = 4; // alto de una línea de texto (mm)
const ALTO_FILA_MIN = 8;

// Anchos que suman exactamente ANCHO_UTIL (277mm).
const COLUMNAS = [
	{ id: 'expediente', label: 'Exp. Proyecto', ancho: 26, tipo: 'texto' },
	{ id: 'organizacion', label: 'Organización', ancho: 46, tipo: 'texto' },
	{ id: 'nombreObra', label: 'Nombre de la obra', ancho: 50, tipo: 'texto' },
	{ id: 'monto', label: 'Monto', ancho: 36, tipo: 'monto' },
	{ id: 'avanceFisico', label: 'Avance Físico', ancho: 34, tipo: 'barra' },
	{ id: 'avanceFinanciero', label: 'Avance Financiero', ancho: 44, tipo: 'barraFinanciera' },
	{ id: 'ubicacion', label: 'Ubicación', ancho: 41, tipo: 'texto' },
];

const ALTO_BARRA = 2.6; // grosor de una barra de avance (mm)
const GAP_BARRA = 1.6; // separación entre barras apiladas (mm)
const ANCHO_TRACK_BARRA = 20; // ancho del "riel" de la barra (mm)
const ANCHO_ETIQUETA_BARRA = 7; // ancho reservado a la etiqueta Conv/A1/A2
const ALTO_METRICAS = 17; // alto del bloque de tarjetas (Inversión/obras/organizaciones)
const GAP_METRICAS = 5; // separación entre las métricas y la tabla

// Colores (mismos que la tabla de la app)
const VERDE = [22, 163, 74]; // avance >= 100
const INDIGO = [79, 70, 229]; // avance > 0
const MORADO = [168, 85, 247]; // adenda 1
const ROSA = [236, 72, 153]; // adenda 2
const GRIS_RIEL = [229, 231, 235];
const GRIS_TEXTO = [55, 65, 81];
const GRIS_SUAVE = [107, 114, 128];
const TEAL_OSCURO = [15, 118, 110];
const TEAL_CLARO = [240, 253, 250];

// Convierte una imagen local a dataURL para poder incrustarla en el PDF
const cargarImagenComoDataUrl = (src) =>
	new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			try {
				const c = document.createElement('canvas');
				c.width = img.naturalWidth;
				c.height = img.naturalHeight;
				c.getContext('2d').drawImage(img, 0, 0);
				resolve({ dataUrl: c.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
			} catch (e) {
				resolve(null);
			}
		};
		img.onerror = () => resolve(null);
		img.src = src;
	});

// Mismo formato que el resto de la app (punto para miles, coma para decimales)
const formatearMonto = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '$ 0,00';
	return '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatearEntero = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '0';
	return n.toLocaleString('es-AR');
};

// Ubicación: partido y localidad. Si son el mismo texto, se muestra una sola vez
// (mismo criterio que el informe de organizaciones).
const getUbicacion = (obra) => {
	const partido = String(obra?.partido_nombre || '').trim();
	const localidad = String(obra?.lc_21_localidad || '').trim();
	if (partido && localidad && partido.toLowerCase() !== localidad.toLowerCase()) {
		return `${partido} — ${localidad}`;
	}
	return partido || localidad || '—';
};

// Líneas de una celda de texto, para poder calcular la altura de la fila.
// splitTextToSize respeta el ancho en el formato actual del documento.
const lineasDeTexto = (doc, texto, ancho) =>
	doc.splitTextToSize(String(texto ?? ''), ancho) || [];

// Dibuja una barra de avance dentro de una celda: etiqueta opcional a la izquierda,
// riel gris + relleno proporcional, y el porcentaje a la derecha.
const dibujarBarra = (doc, x, y, anchoTrack, porcentaje, color, etiqueta) => {
	const pct = Math.max(0, Math.min(100, Number(porcentaje) || 0));
	let xTrack = x;
	if (etiqueta) {
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(6);
		doc.setTextColor(...GRIS_SUAVE);
		doc.text(etiqueta, x, y + ALTO_BARRA - 0.5);
		xTrack = x + ANCHO_ETIQUETA_BARRA;
	}
	// Riel
	doc.setFillColor(...GRIS_RIEL);
	doc.roundedRect(xTrack, y, anchoTrack, ALTO_BARRA, 0.6, 0.6, 'F');
	// Relleno
	if (pct > 0) {
		doc.setFillColor(...color);
		doc.roundedRect(xTrack, y, anchoTrack * (pct / 100), ALTO_BARRA, 0.6, 0.6, 'F');
	}
	// Porcentaje a la derecha
	doc.setFont('helvetica', 'normal');
	doc.setFontSize(7);
	doc.setTextColor(...GRIS_SUAVE);
	doc.text(`${Math.round(pct)}%`, xTrack + anchoTrack + 1.2, y + ALTO_BARRA - 0.5);
};

// Contenido de la celda de Avance Financiero: barra de convenio y, si la obra tiene
// adendas, una barra más por adenda (A1 / A2). Devuelve cuánto alto ocupa.
const dibujarAvanceFinanciero = (doc, obra, x, y) => {
	let yy = y;

	const colorDe = (pct, colorAdenda) => {
		if (pct >= 100) return VERDE;
		return colorAdenda || INDIGO;
	};

	const conv = Number(obra?.detalleConvenio) || 0;
	dibujarBarra(doc, x, yy, ANCHO_TRACK_BARRA, conv, colorDe(conv), 'Conv');
	yy += ALTO_BARRA + GAP_BARRA;

	if (obra?.tieneAdenda1) {
		const a1 = Number(obra?.detalleAdenda1) || 0;
		dibujarBarra(doc, x, yy, ANCHO_TRACK_BARRA, a1, colorDe(a1, MORADO), 'A1');
		yy += ALTO_BARRA + GAP_BARRA;
	}
	if (obra?.tieneAdenda2) {
		const a2 = Number(obra?.detalleAdenda2) || 0;
		dibujarBarra(doc, x, yy, ANCHO_TRACK_BARRA, a2, colorDe(a2, ROSA), 'A2');
		yy += ALTO_BARRA + GAP_BARRA;
	}

	// El último GAP_BARRA sobra (no hay barra después)
	return Math.max(ALTO_BARRA, yy - GAP_BARRA - y);
};

// Contenido de la celda de Monto: igual que el informe por partido. Si hay adenda se
// muestra el monto conveniado, el detalle "+ adenda por ..." y el total.
const lineasDeMonto = (obra) => {
	const monto = Number(obra?.monto_obra_num) || 0;
	const adenda1 = Number(obra?.montoAdenda1_num) || 0;
	const adenda2 = Number(obra?.montoAdenda2_num) || 0;
	const totalAdenda = adenda1 + adenda2;
	const lineas = [{ texto: formatearMonto(monto), negrita: true, color: GRIS_TEXTO }];
	if (totalAdenda > 0) {
		lineas.push({ texto: `+ adenda por ${formatearMonto(totalAdenda)}`, negrita: false, color: GRIS_SUAVE });
		lineas.push({ texto: `Total: ${formatearMonto(monto + totalAdenda)}`, negrita: false, color: GRIS_TEXTO });
	}
	return lineas;
};

// Encabezado de la tabla + fila de datos + paginación.
export const generarInformePdf = async ({ obras, tituloResumen, nombreArchivo }) => {
	if (!obras || obras.length === 0) {
		throw new Error('No hay datos para generar el PDF');
	}

	const doc = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });

	const [encabezado, pie] = await Promise.all([
		cargarImagenComoDataUrl(encabezadoImg),
		cargarImagenComoDataUrl(pieImg),
	]);

	// El encabezado va a todo el ancho útil (es un membrete). El pie se limita en alto:
	// a ancho completo ocuparía ~50mm, un cuarto de la hoja apaisada. Manteniendo su
	// proporción (875x157) y limitando a 25mm de alto queda ~139mm de ancho, centrado.
	let pieAncho = 0;
	let pieAlto = 0;
	if (pie) {
		pieAlto = Math.min(25, ANCHO_UTIL * (pie.height / pie.width));
		pieAncho = pieAlto * (pie.width / pie.height);
	}

	const yEncabezado = MARGEN_TOP;
	const encabezadoAlto = encabezado ? ANCHO_UTIL * (encabezado.height / encabezado.width) : 0;

	// Título y subtítulo (texto, no imagen): el pedido fue que figure
	// "Informe Dirección Provincial de Intervenciones Complementarias".
	const TITULO = 'Informe Dirección Provincial de Intervenciones Complementarias';
	const tituloAlto = 7;
	const subtituloAlto = 5;
	const gapTitulo = 2;

	// Totales del bloque de métricas (mismo criterio que el informe de organizaciones:
	// monto conveniado = monto_obra + adendas).
	const totalInversion = obras.reduce((acc, o) => {
		return acc + (Number(o?.monto_obra_num) || 0)
			+ (Number(o?.montoAdenda1_num) || 0)
			+ (Number(o?.montoAdenda2_num) || 0);
	}, 0);
	const cantidadOrganizaciones = new Set(
		obras
			.map((o) => String(o?.organismo_solicitante || '').trim().toLowerCase())
			.filter(Boolean)
	).size;

	const yTrasSubtitulo = yEncabezado + encabezadoAlto + gapTitulo + tituloAlto + subtituloAlto + 3;
	// La primera página lleva el bloque de métricas; las siguientes empiezan la tabla
	// más arriba (no se repiten los totales en cada hoja).
	const yTablaPrimera = yTrasSubtitulo + ALTO_METRICAS + GAP_METRICAS;
	const yTablaSiguientes = yTrasSubtitulo;

	// Pie de página disponible para la tabla
	const yPieTop = ALTO_PAGINA - MARGEN_BOTTOM - pieAlto;
	const yPieGap = pieAlto > 0 ? 2 : 0;
	const yLimiteContenido = yPieTop - yPieGap;

	const altoHeaderTabla = 8;
	let y = yTablaPrimera;

	// Tres tarjetas con la métrica del informe (Inversión total / Cantidad de obras /
	// Organizaciones), como en /informe-organizaciones.
	const dibujarMetricas = () => {
		const gap = 4;
		const anchoTarjeta = (ANCHO_UTIL - 2 * gap) / 3;
		const metricas = [
			{ label: 'Inversión total', valor: formatearMonto(totalInversion) },
			{ label: 'Cantidad de obras', valor: formatearEntero(obras.length) },
			{ label: 'Organizaciones', valor: formatearEntero(cantidadOrganizaciones) },
		];
		metricas.forEach((m, i) => {
			const x = MARGEN_H + i * (anchoTarjeta + gap);
			doc.setDrawColor(...TEAL_OSCURO);
			doc.setLineWidth(0.5);
			doc.roundedRect(x, yTrasSubtitulo, anchoTarjeta, ALTO_METRICAS, 1.5, 1.5, 'S');
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(7);
			doc.setTextColor(...TEAL_OSCURO);
			doc.text(m.label.toUpperCase(), x + anchoTarjeta / 2, yTrasSubtitulo + 6.5, { align: 'center' });
			doc.setFontSize(14);
			doc.setTextColor(17, 24, 39);
			doc.text(m.valor, x + anchoTarjeta / 2, yTrasSubtitulo + 14, { align: 'center' });
		});
	};

	const dibujarEncabezadoPagina = (esPrimera) => {
		if (encabezado) {
			doc.addImage(encabezado.dataUrl, 'PNG', MARGEN_H, yEncabezado, ANCHO_UTIL, encabezadoAlto);
		}
		// Título
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(14);
		doc.setTextColor(...TEAL_OSCURO);
		doc.text(TITULO, MARGEN_H, yEncabezado + encabezadoAlto + gapTitulo + 5);
		// Subtítulo con los filtros aplicados
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(9);
		doc.setTextColor(...GRIS_TEXTO);
		const subtitulo = `${tituloResumen} — ${formatearEntero(obras.length)} ${
			obras.length === 1 ? 'obra' : 'obras'
		} — fecha de salida: ${new Date().toLocaleDateString('es-AR')}`;
		doc.text(subtitulo, MARGEN_H, yEncabezado + encabezadoAlto + gapTitulo + tituloAlto + 3);
		if (esPrimera) dibujarMetricas();
		// Pie de la imagen, si hay
		if (pie) {
			doc.addImage(pie.dataUrl, 'PNG', (ANCHO_PAGINA - pieAncho) / 2, yPieTop, pieAncho, pieAlto);
		}
	};

	// Cabecera de la tabla (columnas). Se repite en cada página.
	const dibujarHeaderTabla = () => {
		doc.setFillColor(...TEAL_OSCURO);
		doc.rect(MARGEN_H, y, ANCHO_UTIL, altoHeaderTabla, 'F');
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(8);
		doc.setTextColor(255, 255, 255);
		const lineHeightHeader = 3.6;
		let x = MARGEN_H;
		for (const col of COLUMNAS) {
			// Si el título no entra en una línea, se parte en dos y se centra verticalmente
			const lineas = doc.splitTextToSize(col.label, col.ancho - 2 * PAD_CELDA_H);
			const altoTextoHeader = lineas.length * lineHeightHeader;
			const yIni = y + (altoHeaderTabla - altoTextoHeader) / 2 + lineHeightHeader - 0.6;
			for (let i = 0; i < lineas.length; i++) {
				doc.text(lineas[i], x + PAD_CELDA_H, yIni + i * lineHeightHeader);
			}
			x += col.ancho;
		}
		y += altoHeaderTabla;
	};

	dibujarEncabezadoPagina(true);
	dibujarHeaderTabla();

	// Fondo alterno de filas, para poder seguir la lectura
	let indiceFila = 0;

	for (const obra of obras) {
		// --- Preparar el contenido de cada celda ---
		const exp = String(obra?.nroExpProyecto || '—');
		const org = String(obra?.organismo_solicitante || '—');
		const nombre = String(obra?.nombreObra || '—');
		const ubic = getUbicacion(obra);
		const montoLineas = lineasDeMonto(obra);

		// Ancho de texto disponible en cada columna de texto
		const anchoTextoDe = (col) => col.ancho - 2 * PAD_CELDA_H;

		// splitTextToSize mide con la fuente actual: fijarla antes de calcular las
		// líneas para que el wrap coincida con el texto que después se dibuja.
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(7.5);

		const colExp = COLUMNAS[0];
		const colOrg = COLUMNAS[1];
		const colObra = COLUMNAS[2];
		const colMonto = COLUMNAS[3];
		const colFisico = COLUMNAS[4];
		const colFin = COLUMNAS[5];
		const colUbic = COLUMNAS[6];

		const lineasExp = lineasDeTexto(doc, exp, anchoTextoDe(colExp));
		const lineasOrg = lineasDeTexto(doc, org, anchoTextoDe(colOrg));
		const lineasObra = lineasDeTexto(doc, nombre, anchoTextoDe(colObra));
		const lineasUbic = lineasDeTexto(doc, ubic, anchoTextoDe(colUbic));

		// Alto que ocupa cada celda de texto
		const altoTexto = (lineas) => lineas.length * LINE_HEIGHT;

		// Celda de monto: hasta 3 líneas cortas
		const altoMonto = montoLineas.length * (LINE_HEIGHT - 0.6);

		// Celdas de avance: una barra, o tres si hay adendas
		const cantBarrasFin = 1 + (obra?.tieneAdenda1 ? 1 : 0) + (obra?.tieneAdenda2 ? 1 : 0);
		const altoFinanciero = cantBarrasFin * ALTO_BARRA + (cantBarrasFin - 1) * GAP_BARRA;

		const altoContenido = Math.max(
			altoTexto(lineasExp),
			altoTexto(lineasOrg),
			altoTexto(lineasObra),
			altoMonto,
			ALTO_BARRA, // avance físico
			altoFinanciero,
			altoTexto(lineasUbic)
		);
		const altoFila = Math.max(ALTO_FILA_MIN, altoContenido + 2 * PAD_CELDA_V);

		// --- Paginación: si la fila no entra, hoja nueva ---
		if (y + altoFila > yLimiteContenido) {
			doc.addPage();
			y = yTablaSiguientes; // sin métricas: la tabla arranca más arriba
			dibujarEncabezadoPagina(false);
			dibujarHeaderTabla();
		}

		// Fondo de la fila
		if (indiceFila % 2 === 1) {
			doc.setFillColor(...TEAL_CLARO);
			doc.rect(MARGEN_H, y, ANCHO_UTIL, altoFila, 'F');
		}

		// Bordes verticales de las columnas
		doc.setDrawColor(203, 213, 225);
		doc.setLineWidth(0.15);
		let xBorde = MARGEN_H;
		for (const col of COLUMNAS) {
			xBorde += col.ancho;
			if (col !== COLUMNAS[COLUMNAS.length - 1]) {
				doc.line(xBorde, y, xBorde, y + altoFila);
			}
		}
		doc.setDrawColor(203, 213, 225);
		doc.rect(MARGEN_H, y, ANCHO_UTIL, altoFila, 'S');

		const yTextoBase = y + PAD_CELDA_V + LINE_HEIGHT - 1.2;

		// --- Texto de las celdas simples ---
		const escribirLineas = (lineas, xCol) => {
			doc.setFont('helvetica', 'normal');
			doc.setFontSize(7.5);
			doc.setTextColor(...GRIS_TEXTO);
			lineas.forEach((linea, i) => {
				doc.text(linea, xCol + PAD_CELDA_H, yTextoBase + i * LINE_HEIGHT);
			});
		};

		let x = MARGEN_H;
		escribirLineas(lineasExp, x);
		x += colExp.ancho;
		escribirLineas(lineasOrg, x);
		x += colOrg.ancho;
		escribirLineas(lineasObra, x);
		x += colObra.ancho;

		// --- Monto (con desglose de adenda) ---
		montoLineas.forEach((linea, i) => {
			doc.setFont('helvetica', linea.negrita ? 'bold' : 'normal');
			doc.setFontSize(linea.negrita ? 7.5 : 6.5);
			doc.setTextColor(...linea.color);
			doc.text(linea.texto, x + PAD_CELDA_H, yTextoBase + i * (LINE_HEIGHT - 0.6));
		});
		x += colMonto.ancho;

		// --- Avance físico (una barra) ---
		const fisico = Number(obra?.avanceFisico) || 0;
		dibujarBarra(
			doc,
			x + PAD_CELDA_H,
			y + altoFila / 2 - ALTO_BARRA / 2,
			ANCHO_TRACK_BARRA,
			fisico,
			fisico >= 100 ? VERDE : INDIGO
		);
		x += colFisico.ancho;

		// --- Avance financiero (convenio + adendas) ---
		dibujarAvanceFinanciero(
			doc,
			obra,
			x + PAD_CELDA_H,
			y + (altoFila - altoFinanciero) / 2
		);
		x += colFin.ancho;

		// --- Ubicación ---
		escribirLineas(lineasUbic, x);

		y += altoFila;
		indiceFila += 1;
	}

	doc.save(`${nombreArchivo || 'informe'}.pdf`);
};
